/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {CVSAssetType, User} from '@ion/core/src/types/entities';
import {BaseMapEnum} from '@ion/dls/src/types/types';

// input selectors ...
const selectIsInitialized = ({dashboard: {isInitialized}}) => isInitialized;
const selectLoading = (slice: string) => ({dashboard}) => dashboard[slice].loading;
const selectError = (slice: string) => ({dashboard}) => dashboard[slice].error;
const selectAllAssets = ({dashboard: {assets: {all}}}) => all;
const selectSelectedAsset = ({dashboard: {assets: {selected}}}) => selected;
const selectAssetsLoaded = ({dashboard: {assets: {loaded}}}) => loaded;
const selectAssetsError = ({dashboard: {assets: {error}}}) => error;
const selectCurrentStateError = ({dashboard: {assets: {currentStateError}}}) => currentStateError;
const selectMapInitialized = ({dashboard: {map}}) => map.initialized;
const selectBasemap = ({dashboard: {map}}) => map.basemap;
const selectExtent = ({dashboard: {map}}) => map.extent;
const selectCenter = ({dashboard: {map}}) => map.center;
const selectZoom = ({dashboard: {map}}) => map.zoom;
const selectAssetDialog = ({dashboard: {assetDialog: {show}}}) => show;
const selectSubscriptionDialog = ({dashboard: {subscriptionDialog: {show}}}) => show;
const selectEmergencyDialog = ({dashboard: {emergencyDialog: {show}}}) => show;
const selectUserProfileDialog = ({dashboard: {userProfileDialog:  {show}}}) => show;
const selectAllUsers = ({dashboard: {users: {all}}}) => all;
const selectUsersLoaded = ({dashboard: {users: {loaded}}}) => loaded;
const selectAddUserLoading = selectLoading('addUser');
const selectEditUserLoading = selectLoading('editUser');
const selectEditAssetLoading = selectLoading('edit');
const selectAddUserError = selectError('addUser');
const selectEditUserError = selectError('editUser');
const selectEditAssetError = selectError('edit');
const selectSettings = ({dashboard}) => dashboard.settings;
const selectSubscriptionPlans = ({dashboard}) => dashboard.subscriptionPlans;
const selectPayementUrl = ({dashboard}) => dashboard.paymentUrl;

// output selectors ...
export const isDashboardInitializedSelector = createSelector(selectIsInitialized,
  (isInitialized: boolean): boolean => isInitialized
);

export const assetsSelector = createDeepEqualSelector(selectAllAssets,
  (all: CVSAssetType[]): CVSAssetType[] => all
);

export const selectedAssetSelector = createDeepEqualSelector(selectSelectedAsset,
  (selected: CVSAssetType): CVSAssetType => selected
);

export const assetsLoadedSelector = createSelector(selectAssetsLoaded,
  (loaded: boolean): boolean => loaded
);

export const assetsErrorSelector = createSelector(selectAssetsError,
  (error: boolean): boolean => error
);

export const currentStateErrorSelector = createSelector(selectCurrentStateError,
  (currentStateError: boolean): boolean => currentStateError
);

export const mapInitializedSelector = createSelector(selectMapInitialized,
  (initialized: boolean): boolean => initialized
);

export const mapBasemapSelector = createDeepEqualSelector(selectBasemap,
  (basemap: BaseMapEnum): BaseMapEnum => basemap
);

export const mapZoomSelector = createSelector(selectZoom,
  (zoom: number): number => zoom
);

export const mapExtentSelector = createDeepEqualSelector(selectExtent,
  (extent: any): any => extent
);

export const mapCenterSelector = createDeepEqualSelector(selectCenter,
  (center: any): any => center
);

export const assetDialogSelector = createSelector(selectAssetDialog,
  (show: boolean): boolean => show
);

export const subscriptionDialogSelector = createSelector(selectSubscriptionDialog,
  (show: boolean): boolean => show
);

export const emergencyDialogSelector = createSelector(selectEmergencyDialog,
  (show: boolean): boolean => show
);

export const userProfileDialogSelector = createSelector(selectUserProfileDialog,
  (show: boolean): boolean => show
);

export const usersSelector = createDeepEqualSelector(selectAllUsers,
  (all: User[]): User[] => all
);

export const usersLoadedSelector = createSelector(selectUsersLoaded,
  (loaded: boolean): boolean => loaded
);

export const addUserLoadingSelector = createSelector(selectAddUserLoading,
  (loading: boolean): boolean => loading
);

export const editUserLoadingSelector = createSelector(selectEditUserLoading,
  (loading: boolean): boolean => loading
);

export const editAssetLoadingSelector = createSelector(selectEditAssetLoading,
  (loading: boolean): boolean => loading
);

export const addUserErrorSelector = createSelector(selectAddUserError,
  (error: boolean): boolean => error
);

export const editUserErrorSelector = createSelector(selectEditUserError,
  (error: boolean): boolean => error
);

export const editAssetErrorSelector = createSelector(selectEditAssetError,
  (error: boolean): boolean => error
);

export const settingsSelector = createDeepEqualSelector(selectSettings,
  (settings) => settings
);

export const subscriptionPlansSelector = createDeepEqualSelector(selectSubscriptionPlans,
  (subscriptionPlans) => subscriptionPlans
);

export const paymentUrlSelector = createSelector(selectPayementUrl,
  (paymentUrl: string): string => paymentUrl
);
